import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { API } from '../App';
import { Trophy, TrendingUp, Star, ArrowRight } from 'lucide-react';

const Results = () => {
  const [results, setResults] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [statsData, setStatsData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/public/results`),
      axios.get(`${API}/public/testimonials`),
      axios.get(`${API}/public/site-sections/results_stats`).catch(() => ({ data: null }))
    ]).then(([resultsRes, testimonialsRes, statsRes]) => {
      setResults(resultsRes.data);
      setTestimonials(testimonialsRes.data);
      setStatsData(statsRes.data);
      setLoading(false);
    }).catch(err => {
      console.error(err);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-electric-cyan"></div>
      </div>
    );
  }

  const statsContent = statsData?.content || {};
  const defaultStats = [
    { value: '95%', label: 'Success Rate' },
    { value: '500+', label: 'Students Taught' },
    { value: '90%', label: 'Score Improvement' },
    { value: '10+', label: 'Years Experience' }
  ];

  return (
    <div className="py-16 md:py-24">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-electric-cyan/10 border border-electric-cyan/30 rounded-full px-4 py-2 mb-6">
            <Trophy className="w-4 h-4 text-electric-cyan" />
            <span className="text-electric-cyan text-sm font-medium">Proven Track Record</span>
          </div>
          <h1 className="font-outfit text-4xl md:text-6xl font-bold text-white mb-4" data-testid="results-title">
            Student Results
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto">
            Our students consistently achieve outstanding scores across all boards
          </p>
        </motion.div>

        {/* Stats Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16"
        >
          {(statsContent.stats || defaultStats).map((stat, idx) => (
            <div key={idx} className="glass-card p-6 text-center">
              <div className="text-3xl md:text-4xl font-outfit font-bold text-electric-cyan mb-1">{stat.value}</div>
              <div className="text-slate-400 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        {/* Results Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-24"
        >
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Recent Achievements
          </h2>
          
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {results.map((result, idx) => (
              <motion.div
                key={result.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1 + idx * 0.05 }}
                className="glass-card p-6 text-center card-hover"
              >
                <div className="w-12 h-12 rounded-full bg-electric-cyan/20 flex items-center justify-center mx-auto mb-3">
                  <Trophy className="w-6 h-6 text-electric-cyan" />
                </div>
                <div className="text-4xl font-outfit font-bold text-electric-cyan mb-2">{result.score}</div>
                <div className="text-white font-medium mb-1">{result.subject}</div>
                {result.improvement && (
                  <div className="inline-flex items-center gap-1 text-green-400 text-sm">
                    <TrendingUp className="w-4 h-4" />
                    {result.improvement}
                  </div>
                )}
                <div className="text-slate-500 text-xs mt-2">{result.year}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Testimonials */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <h2 className="font-outfit text-2xl md:text-3xl font-bold text-white mb-8 text-center">
            Student Testimonials
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <motion.div
                key={testimonial.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="glass-card p-6 card-hover"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-300 mb-6 italic leading-relaxed">"{testimonial.content}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-electric-cyan/20 flex items-center justify-center">
                    <span className="text-electric-cyan font-medium">{testimonial.student_name?.charAt(0)}</span>
                  </div>
                  <div>
                    <div className="text-white font-medium">{testimonial.student_name}</div>
                    <div className="text-slate-400 text-sm">{testimonial.school}</div>
                    <div className="text-slate-500 text-xs">{testimonial.grade}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card p-12 text-center mt-24 relative overflow-hidden"
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-electric-cyan to-transparent"></div>
          <h2 className="font-outfit text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Join Our Success Stories?
          </h2>
          <p className="text-slate-400 mb-8 max-w-xl mx-auto">
            Start your journey to academic excellence today
          </p>
          <Link to="/register" className="btn-primary inline-flex items-center gap-2" data-testid="results-cta">
            Register Now <ArrowRight className="w-4 h-4" />
          </Link>
        </motion.div>
      </div>
    </div>
  );
};

export default Results;
